/**
 * Klaviyo star rating widget wrapper.
 *
 * Klaviyo scans for empty `.klaviyo-star-rating-widget` nodes once when its module
 * mounts (no re-scan of its own), so the markup is authored alongside the element
 * (Liquid on the PDP, JSX in SearchSpring cards) and this element never overwrites it.
 * Cards render after Klaviyo's initial scan, so an unfilled widget asks Klaviyo to
 * re-render via `window.klReviewsObject.renderWidgets()`, then observes for the count.
 */

export const KLAVIYO_STAR_RATING_READY_EVENT = 'klaviyo-star-rating:ready'

type KlReviewsObject = { renderWidgets?: () => void }

const RENDER_DEBOUNCE_MS = 150
const RENDER_POLL_MS = 300
const RENDER_MAX_ATTEMPTS = 20

let renderTimer: ReturnType<typeof setTimeout> | null = null
let renderAttempts = 0

function requestKlaviyoRender() {
  if (renderTimer) return
  renderTimer = setTimeout(runKlaviyoRender, RENDER_DEBOUNCE_MS)
}

function runKlaviyoRender() {
  renderTimer = null
  const klReviews = (window as unknown as { klReviewsObject?: KlReviewsObject }).klReviewsObject

  if (klReviews?.renderWidgets) {
    renderAttempts = 0
    klReviews.renderWidgets()
    return
  }

  if (renderAttempts < RENDER_MAX_ATTEMPTS) {
    renderAttempts += 1
    renderTimer = setTimeout(runKlaviyoRender, RENDER_POLL_MS)
  } else {
    renderAttempts = 0
  }
}

export class KlaviyoStarRatingElement extends HTMLElement {
  private observer: MutationObserver | null = null
  private reviewCountSpan: HTMLSpanElement | null = null
  private hasNotifiedReady = false

  connectedCallback() {
    if (!this.querySelector('.klaviyo-star-rating-widget')) {
      this.render()
    }
    this.reviewCountSpan = this.querySelector('.custom-star-rating')
    this.observeKlaviyoWidget()
  }

  disconnectedCallback() {
    this.cleanup()
  }

  private render() {
    const productId = this.getAttribute('product-id') || ''
    const productTitle = this.getAttribute('product-title') || ''
    const productType = this.getAttribute('product-type') || ''

    this.innerHTML = `
      <div class="klaviyo-star-rating-container flex flex-nowrap items-center lg:justify-end gap-x-0.25">
        <div
          class="klaviyo-star-rating-widget flex"
          slot="productReviews"
          data-id="${productId}"
          data-product-title="${productTitle}"
          data-product-type="${productType}"
        ></div>
        <span class="custom-star-rating paragraph-xs text-type-black-primary"></span>
      </div>
    `
  }

  private observeKlaviyoWidget() {
    const targetNode = this.querySelector('.klaviyo-star-rating-widget')
    if (!targetNode) return

    if (this.handleKlaviyoContent(targetNode)) return

    this.observer = new MutationObserver(() => {
      this.handleKlaviyoContent(targetNode)
    })
    this.observer.observe(targetNode, { childList: true, subtree: true })

    requestKlaviyoRender()
  }

  private handleKlaviyoContent(targetNode: Element): boolean {
    if (targetNode.querySelector('.kl_reviews__star_rating_widget')) {
      this.notifyReady()
    }

    const reviewLabel = targetNode.querySelector('.kl_reviews__star_rating_widget__label')
    if (reviewLabel && this.reviewCountSpan) {
      const originalText = reviewLabel.textContent?.trim() || ''
      const count = originalText.replace(/\breviews?\b/gi, '').trim()
      this.reviewCountSpan.textContent = `(${count})`
      this.cleanup()
      return true
    }

    return false
  }

  private notifyReady() {
    if (this.hasNotifiedReady) return
    this.hasNotifiedReady = true
    this.dispatchEvent(
      new CustomEvent(KLAVIYO_STAR_RATING_READY_EVENT, { bubbles: true })
    )
  }

  private cleanup() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }
}
